import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { FaSearch, FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import { product } from '../utils/sjt_product';
import { WA_NUMBER } from '../constant/detail';

const CATEGORIES = [
  { id: 'all', label: 'Semua Produk' },
  { id: 'chemical', label: 'Chemical Pool' },
  { id: 'filter-media', label: 'Filter Media' },
  { id: 'accessories', label: 'Accessories & Tools' },
];

const CATEGORY_INFO = {
  chemical: {
    title: 'Chemical Pool Bali',
    desc: 'Kami menyediakan bahan kimia kolam renang (chemical pool) berkualitas tinggi untuk kebutuhan perawatan rutin maupun shock treatment. Semua produk chemical pool kami adalah original dengan konsentrasi aktif yang terstandarisasi — tidak ada produk palsu atau encerkan. Tersedia dalam berbagai kemasan mulai dari 1 kg hingga karung/pail untuk kebutuhan villa, hotel, maupun kontraktor kolam renang di Bali.',
  },
  'filter-media': {
    title: 'Filter Media Kolam Renang',
    desc: 'Media filter adalah komponen krusial dalam sistem filtrasi sand filter kolam renang. Pasir silika berkualitas tinggi menyaring partikel 20–100 mikron secara efektif, menjaga kejernihan air kolam. Garam industri tersedia untuk sistem salt chlorinator yang semakin populer di villa dan resort Bali karena menghasilkan klorin alami dari air garam.',
  },
  accessories: {
    title: 'Accessories & Peralatan Kolam Renang Bali',
    desc: 'Peralatan dan aksesori kolam renang yang lengkap dan berkualitas untuk memudahkan perawatan rutin. Mulai dari vacuum head, selang vacuum, sikat alga, skimmer daun, hingga test kit kualitas air. Semua peralatan tersedia ready stock di toko kami di Ubud dan Lodtunduh, Gianyar, Bali.',
  },
};

const relatedServices = [
  {
    label: 'Jasa Maintenance Kolam Renang Bali',
    to: '/jasa-maintenance-kolam-renang-bali',
  },
  { label: 'Jual Chemical Pool Bali', to: '/jual-chemical-pool-bali' },
  {
    label: 'Pasang Filter Kolam Renang Bali',
    to: '/pasang-filter-kolam-renang-bali',
  },
  { label: 'Jasa Testkit Air Kolam Bali', to: '/jasa-testkit-air-kolam-bali' },
  {
    label: 'Konsultasi Kolam Renang Bali',
    to: '/konsultasi-kolam-renang-bali',
  },
];

const faqs = [
  {
    q: 'Apakah bisa beli chemical pool satuan (tidak harus karung)?',
    a: 'Ya, kami melayani pembelian satuan (kg/liter) maupun grosir (karung/pail). Tidak ada minimum order untuk pembelian retail di toko kami di Ubud dan Lodtunduh.',
  },
  {
    q: 'Berapa lama stok tersedia?',
    a: 'Hampir semua produk ready stock di toko kami. Untuk order dalam jumlah besar (>100 kg), sebaiknya konfirmasi dulu via WhatsApp agar kami bisa menyiapkan stok yang sesuai.',
  },
  {
    q: 'Apakah ada konsultasi dosing chemical?',
    a: 'Tentu. Tim kami siap memberikan konsultasi gratis tentang jenis dan dosis chemical yang tepat berdasarkan volume kolam, kondisi air, dan frekuensi penggunaan.',
  },
];

const Product = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = product.filter((item) => {
    const matchSearch = item.product_name
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchCategory =
      activeCategory === 'all' || item.category === activeCategory;
    return matchSearch && matchCategory;
  });

  const categoryInfo = CATEGORY_INFO[activeCategory];

  const productListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Produk Kolam Renang Bali - Pool Shop Surya Jaya Teknik',
    itemListElement: product.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.product_name,
      description: p.description,
    })),
  };

  return (
    <div className="bg-gray-50 mt-16">
      <Head>
        <title>
          Produk Kolam Renang Bali | Pompa, Filter, Chemical | Surya Jaya Teknik
        </title>
        <meta
          name="description"
          content="Jual produk kolam renang Bali lengkap: chemical pool, pasir silika, pompa, filter, skimmer, vacuum & test kit. Harga terbaik, stok ready. Order via WhatsApp!"
        />
        <link
          rel="canonical"
          href="https://poolshopsuryajayateknik.com/product"
        />
        <meta
          property="og:title"
          content="Produk Kolam Renang Bali | Chemical, Filter & Accessories | Surya Jaya Teknik"
        />
        <meta
          property="og:description"
          content="Jual produk kolam renang Bali lengkap: chemical pool, pasir silika, filter, skimmer, vacuum & test kit. Harga terbaik, stok ready."
        />
        <meta
          property="og:url"
          content="https://poolshopsuryajayateknik.com/product"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://poolshopsuryajayateknik.com/img/product-1.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(productListSchema)}
        </script>
      </Head>

      {/* ── Hero Section ── */}
      <section className="bg-tertiary text-white py-4 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block text-xs font-semibold text-primary uppercase tracking-widest mb-3">
            Pool Shop Surya Jaya Teknik
          </span>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Produk Kolam Renang Bali - Chemical, Filter &amp; Accessories
          </h1>
          <p className="text-primary/80 text-base md:text-lg mb-8">
            Toko kolam renang terlengkap di Bali. Melayani villa, hotel, resort,
            dan kontraktor kolam renang di seluruh Bali sejak 2019. Stok ready,
            harga transparan, konsultasi gratis.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm font-semibold">
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-primary" /> 25+ Produk Tersedia
            </span>
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-primary" /> Stok Ready di 2 Toko
            </span>
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-primary" /> Bisa konsultasi ke toko
            </span>
          </div>
        </div>
      </section>

      {/* ── Product Catalog ── */}
      <section className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSearch('');
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition min-h-[40px] ${
                  activeCategory === cat.id
                    ? 'bg-tertiary text-white'
                    : 'border border-tertiary/30 text-tertiary hover:bg-tertiary/10'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Category description (SEO content) */}
          {categoryInfo && (
            <div className="bg-white rounded-2xl border border-primary/20 p-5 mb-8">
              <h2 className="font-bold text-tertiary text-lg mb-2">
                {categoryInfo.title}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {categoryInfo.desc}
              </p>
            </div>
          )}

          {/* Search + Order bar */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-8">
            <div className="flex items-center bg-white rounded-full shadow-sm border border-gray-200 flex-1">
              <FaSearch className="ml-4 text-gray-400 flex-shrink-0" />
              <input
                className="py-3 px-3 focus:outline-none flex-1 bg-transparent text-sm"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari produk kolam renang..."
                aria-label="Cari produk"
              />
            </div>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
                'Halo, saya ingin order produk kolam renang:'
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Order produk via WhatsApp"
              className="flex items-center justify-center gap-2 bg-green-600 hover:opacity-90 text-white font-semibold px-6 py-3 rounded-full transition text-sm min-h-[44px]"
            >
              <FaWhatsapp />
              Order via WhatsApp
            </a>
          </div>

          {/* Product count */}
          <p className="text-sm text-gray-400 mb-5">
            Menampilkan{' '}
            <span className="font-semibold text-tertiary">
              {filtered.length}
            </span>{' '}
            produk
            {activeCategory !== 'all' &&
              ` dalam kategori "${
                CATEGORIES.find((c) => c.id === activeCategory)?.label
              }"`}
          </p>

          {/* Product grid */}
          {filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-16">
              Produk tidak ditemukan. Coba kata kunci lain.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition"
                >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img
                      src={item.img}
                      alt={`${item.product_name} - Produk Kolam Renang Bali`}
                      className="w-full h-full object-contain p-3"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-tertiary text-sm mb-1 leading-snug">
                      {item.product_name}
                    </h3>
                    <p className="text-xs text-gray-400 mb-1">
                      {item.type} · {item.package}
                    </p>
                    {item.description && (
                      <p className="text-xs text-gray-500 leading-relaxed mt-1">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-tertiary mb-8 text-center">
            Pertanyaan Umum Seputar Produk Kolam Renang
          </h2>
          <div className="space-y-4">
            {faqs.map((item) => (
              <div
                key={item.q}
                className="border border-gray-200 rounded-xl p-5"
              >
                <h3 className="font-semibold text-tertiary mb-2 text-sm">
                  {item.q}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Related Services ── */}
      <section className="py-10 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-bold text-tertiary mb-4">
            Layanan Terkait
          </h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Product;
